import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { UIActions, UIState, UIStore, UISetter } from './ui.store.types';
import { logger } from '@/lib/utils/logger';

const initialState: UIState = {
  // Side Panel State
  sidePanelType: null,
  sidePanelLoading: false,
  sidePanelError: null,
  sidePanelLayoutMode: null,
  lastNonNullLayoutMode: 'split',

  // Agent Modal State
  isAgentModalOpen: false,
  modalAgentId: null,
  onBeforeAgentSwitch: undefined,
  modalLoading: false,
  modalError: null,
};

// Side Panel Actions
function makeSidePanelActions(
  set: UISetter,
): Pick<
  UIActions,
  | 'openSidePanel'
  | 'closeSidePanel'
  | 'setSidePanelLoading'
  | 'setSidePanelError'
  | 'setSidePanelLayoutMode'
> {
  return {
    openSidePanel: (type, layoutMode = 'split') => {
      set((state) => {
        // Only update lastNonNullLayoutMode if layoutMode is not null
        const newLastNonNullLayoutMode =
          layoutMode === 'split' || layoutMode === 'fullscreen'
            ? layoutMode
            : state.lastNonNullLayoutMode;

        return {
          ...state,
          sidePanelType: type,
          sidePanelError: null,
          sidePanelLayoutMode: layoutMode,
          lastNonNullLayoutMode: newLastNonNullLayoutMode,
        };
      });
    },

    closeSidePanel: () => {
      set((state) => ({
        ...state,
        sidePanelType: null,
        sidePanelLoading: false,
        sidePanelError: null,
        sidePanelLayoutMode: initialState.sidePanelLayoutMode,
      }));
    },

    setSidePanelLoading: (loading) => {
      set((state) => ({
        ...state,
        sidePanelLoading: loading,
      }));
    },

    setSidePanelError: (error) => {
      set((state) => ({
        ...state,
        sidePanelError: error,
        sidePanelLoading: false,
      }));
    },

    setSidePanelLayoutMode: (layoutMode) => {
      set((state) => {
        // Only update lastNonNullLayoutMode if layoutMode is not null
        const newLastNonNullLayoutMode =
          layoutMode === 'split' || layoutMode === 'fullscreen'
            ? layoutMode
            : state.lastNonNullLayoutMode;

        return {
          ...state,
          sidePanelLayoutMode: layoutMode,
          lastNonNullLayoutMode: newLastNonNullLayoutMode,
        };
      });
    },
  };
}

// Agent Modal Actions
function makeAgentModalActions(
  set: UISetter,
): Pick<
  UIActions,
  | 'openAgentModal'
  | 'closeAgentModal'
  | 'setModalAgentId'
  | 'setOnBeforeAgentSwitch'
  | 'setModalLoading'
  | 'setModalError'
> {
  return {
    openAgentModal: (agentId, onBeforeHandler) => {
      set((state) => ({
        ...state,
        isAgentModalOpen: true,
        modalAgentId: agentId,
        onBeforeAgentSwitch: onBeforeHandler,
        modalError: null,
      }));
    },

    closeAgentModal: () => {
      set((state) => ({
        ...state,
        isAgentModalOpen: false,
        modalAgentId: null,
        onBeforeAgentSwitch: undefined,
        modalLoading: false,
        modalError: null,
      }));
    },

    setModalAgentId: (agentId) => {
      set((state) => ({
        ...state,
        modalAgentId: agentId,
      }));
    },

    setOnBeforeAgentSwitch: (callback) => {
      set((state) => ({
        ...state,
        onBeforeAgentSwitch: callback,
      }));
    },

    setModalLoading: (loading) => {
      set((state) => ({
        ...state,
        modalLoading: loading,
      }));
    },

    setModalError: (error) => {
      set((state) => ({
        ...state,
        modalError: error,
        modalLoading: false,
      }));
    },
  };
}

// Global Reset Actions
function makeResetActions(
  set: UISetter,
): Pick<UIActions, 'resetUIState' | 'resetSidePanelState' | 'resetModalState'> {
  return {
    resetUIState: () => {
      set(() => initialState);
    },

    resetSidePanelState: () => {
      set((state) => ({
        ...state,
        sidePanelType: null,
        sidePanelLoading: false,
        sidePanelError: null,
        sidePanelLayoutMode: initialState.sidePanelLayoutMode,
      }));
    },

    resetModalState: () => {
      set((state) => ({
        ...state,
        isAgentModalOpen: false,
        modalAgentId: null,
        onBeforeAgentSwitch: undefined,
        modalLoading: false,
        modalError: null,
      }));
    },
  };
}

function isSidePanelOpen(state: UIState) {
  return state.sidePanelType != null;
}

// Create the UI store
export const useUIStore = create<UIStore>((set) => ({
  ...initialState,
  ...makeSidePanelActions(set),
  ...makeAgentModalActions(set),
  ...makeResetActions(set),
}));

// PERFORMANCE OPTIMIZED SELECTORS

// Side Panel Selectors - Granular for optimal re-rendering
export const useSidePanelType = () => useUIStore((state) => state.sidePanelType);
export const useCloseSidePanel = () => useUIStore((state) => state.closeSidePanel);
export const useSidePanelLayoutMode = () =>
  useUIStore(
    useShallow(({ sidePanelLayoutMode, setSidePanelLayoutMode }) => ({
      sidePanelLayoutMode,
      setSidePanelLayoutMode,
    })),
  );
export const useLastNonNullLayoutMode = () => useUIStore((state) => state.lastNonNullLayoutMode);
export const useIsSidePanelOpen = () => useUIStore(isSidePanelOpen);

// Optimized: Memoized action selectors to prevent unnecessary re-renders
export const useSidePanelActions = () =>
  useUIStore(
    useShallow((state) => ({
      openSidePanel: state.openSidePanel,
      closeSidePanel: state.closeSidePanel,
      setSidePanelLoading: state.setSidePanelLoading,
      setSidePanelError: state.setSidePanelError,
      resetSidePanelState: state.resetSidePanelState,
    })),
  );

// Optimized: Combined selector for components that need multiple side panel values
export const useSidePanel = () =>
  useUIStore(
    useShallow((state) => ({
      isSidePanelOpen: isSidePanelOpen(state),
      sidePanelType: state.sidePanelType,
      loading: state.sidePanelLoading,
      error: state.sidePanelError,
      sidePanelLayoutMode: state.sidePanelLayoutMode,
      openSidePanel: state.openSidePanel,
      closeSidePanel: state.closeSidePanel,
      setSidePanelLayoutMode: state.setSidePanelLayoutMode,
    })),
  );

// Optimized: Memoized action selectors
export const useAgentModalActions = () =>
  useUIStore(
    useShallow((state) => ({
      openAgentModal: state.openAgentModal,
      closeAgentModal: state.closeAgentModal,
      setModalAgentId: state.setModalAgentId,
      setOnBeforeAgentSwitch: state.setOnBeforeAgentSwitch,
      setModalLoading: state.setModalLoading,
      setModalError: state.setModalError,
      resetModalState: state.resetModalState,
    })),
  );

// Optimized: Combined selector for components that need multiple modal values
export const useAgentModal = () =>
  useUIStore(
    useShallow((state) => ({
      isModalOpen: state.isAgentModalOpen,
      modalAgentId: state.modalAgentId,
      onBeforeAgentSwitch: state.onBeforeAgentSwitch,
      loading: state.modalLoading,
      error: state.modalError,
      openAgentModal: state.openAgentModal,
      closeAgentModal: state.closeAgentModal,
    })),
  );

// Global UI Actions - Optimized
export const useUIActions = () =>
  useUIStore(
    useShallow((state) => ({
      resetUIState: state.resetUIState,
      resetSidePanelState: state.resetSidePanelState,
      resetModalState: state.resetModalState,
    })),
  );

// Legacy Compatibility Selectors (for gradual migration)
// Note: These can be removed once all components are migrated
export const useSidePanelContext = () =>
  useUIStore(
    useShallow((state) => ({
      sidePanelType: state.sidePanelType,
      openSidePanel: state.openSidePanel,
      closeSidePanel: state.closeSidePanel,
    })),
  );

export const useAgentModalContext = () =>
  useUIStore(
    useShallow((state) => ({
      isModalOpen: state.isAgentModalOpen,
      modalAgentId: state.modalAgentId,
      openAgentModal: state.openAgentModal,
      closeAgentModal: state.closeAgentModal,
      switchAgentAndClose: () => {
        // This will be implemented by components using the agent store
        state.closeAgentModal();
      },
    })),
  );

// Subscribe to UI store changes for debugging/logging
if (process.env.NODE_ENV === 'development') {
  useUIStore.subscribe((state) => {
    logger.debug(
      `[UIStore] SidePanel: ${state.sidePanelType ?? 'closed'} | AgentModal: ${
        state.isAgentModalOpen ? `open (${state.modalAgentId})` : 'closed'
      }`,
    );
  });
}

if (import.meta.env.DEV) {
  window.useUIStore = useUIStore;
}
